import { Stack, Typography, Box, FormControl, TextField } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { StatusCheckPredicateConfiguration } from 'components/status-checks/form/status-check/StatusCheckPredicateConfiguration'
import { TestStatusCheckSuccessEvaluation } from 'components/status-checks/form/status-check/TestStatusCheckSuccessEvaluation'
import {
  type StatusCheckFormValues,
  buildEvaluationConfigurationFromFormValues,
  MIN_OK_LATENCY_MS
} from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'

export const StatusCheckSuccessEvaluation: React.FC = () => {
  const {
    endpointResponse,
    isTestSuccessEvaluationDisabled,
    shouldDisplayPredicateConfiguration,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  const { control, formState, getValues, setValue } =
    useFormContext<StatusCheckFormValues>()

  // Set the predicates field as an empty array if the predicate configuration is not displayed.
  useEffect(() => {
    if (shouldDisplayPredicateConfiguration) return

    setValue('evaluationConfiguration.responseBodyEvaluation.predicates', [])
  }, [shouldDisplayPredicateConfiguration, setValue])

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h6">Success Evaluation</Typography>
        <Typography>
          This criteria will tell us if the target or service is weathering the
          tests.
        </Typography>
      </Stack>
      <Box>
        <Stack ml={2} spacing={2}>
          <FormControl sx={{ width: '50%' }}>
            <Controller
              control={control}
              name="healthyStatusCode"
              render={({ field }) => {
                const { healthyStatusCode } = formState.errors || {}
                const isDisabled = Boolean(!endpointResponse)
                const hasError = Boolean(healthyStatusCode)
                const helperText =
                  healthyStatusCode?.message ??
                  'Use a single status code (200) or a range (200-299)'

                return (
                  <TextField
                    {...field}
                    disabled={isDisabled}
                    error={hasError}
                    helperText={helperText}
                    label="Healthy Status Code"
                    variant="outlined"
                    onChange={(e) => {
                      const formValues = {
                        ...getValues(),
                        healthyStatusCode: e.target.value
                      }

                      setValue(
                        'evaluationConfiguration',
                        buildEvaluationConfigurationFromFormValues(formValues)
                      )

                      field.onChange(e)
                    }}
                  />
                )
              }}
            />
          </FormControl>
          <FormControl sx={{ width: '50%' }}>
            <Controller
              control={control}
              name="requestTimeout"
              render={({ field }) => {
                const { requestTimeout } = formState.errors || {}
                const isDisabled = Boolean(!endpointResponse)
                const hasError = Boolean(requestTimeout)
                const helperText =
                  requestTimeout?.message ??
                  'Optional - Maximum time in milliseconds to wait for a response'

                return (
                  <TextField
                    {...field}
                    disabled={isDisabled}
                    error={hasError}
                    helperText={helperText}
                    inputProps={{ min: MIN_OK_LATENCY_MS }}
                    label="Request Timeout"
                    type="number"
                    value={field.value === null ? '' : field.value}
                    variant="outlined"
                    onChange={(e) => {
                      const value = e.target.value
                      // Necessary because this value is converted to a string at the DOM level
                      const newRequestTimeout =
                        value === '' ? null : parseInt(value)
                      const formValues = {
                        ...getValues(),
                        requestTimeout: newRequestTimeout
                      }

                      setValue(
                        'evaluationConfiguration',
                        buildEvaluationConfigurationFromFormValues(formValues)
                      )

                      field.onChange(newRequestTimeout)
                    }}
                  />
                )
              }}
            />
          </FormControl>
          {shouldDisplayPredicateConfiguration && (
            <StatusCheckPredicateConfiguration />
          )}
          <TestStatusCheckSuccessEvaluation
            disabled={isTestSuccessEvaluationDisabled}
            endpointResponse={endpointResponse}
            onChanged={setIsStatusCheckEvaluationValidated}
          />
        </Stack>
      </Box>
    </Stack>
  )
}
